import { useEffect, useState } from 'react';
import { marketplacesyncApi, type DeadLetterEntry, type Feed, type PublicationJob, type RetryTask, type SyncJob, type Connector, type WebhookEvent, type MarketplaceChannel } from '../../api/marketplacesync';

const CHANNELS: MarketplaceChannel[] = ['amazon_spapi', 'flipkart', 'myntra', 'ajio', 'shopify', 'woocommerce'];

export function PublicationQueuePage() {
  const [jobs, setJobs] = useState<PublicationJob[]>([]);
  const [listingId, setListingId] = useState('');
  const [channel, setChannel] = useState<MarketplaceChannel>('amazon_spapi');
  const [error, setError] = useState('');

  const reload = () => void marketplacesyncApi.listPublications().then(setJobs).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const onPublish = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await marketplacesyncApi.createPublication({ listing_id: listingId, product_id: listingId, channel, action: 'publish' });
      setListingId('');
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Publication Queue</h1>
      <form onSubmit={onPublish} className="mt-6 flex flex-wrap gap-3 rounded-xl border bg-white p-4">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} required />
        <select className="rounded border px-3 py-2 text-sm" value={channel} onChange={(e) => setChannel(e.target.value as MarketplaceChannel)}>
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Publish</button>
      </form>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 w-full text-left text-sm">
        <thead><tr className="border-b"><th className="py-2">Listing</th><th>Channel</th><th>Status</th><th>Retries</th><th /></tr></thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} className="border-b">
              <td className="py-2 font-mono text-xs">{j.listing_id}</td>
              <td>{j.channel}</td>
              <td>{j.status}</td>
              <td>{j.retry_count}</td>
              <td><button type="button" className="text-blue-600" onClick={() => void marketplacesyncApi.retryPublication(j.id).then(reload)}>Retry</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FeedMonitorPage() {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  useEffect(() => { void marketplacesyncApi.listFeeds().then(setFeeds); }, []);
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Feed Monitor</h1>
      <ul className="mt-6 space-y-2">
        {feeds.map((f) => (
          <li key={f.id} className="rounded border bg-white p-3 text-sm">
            {f.channel} · {f.feed_type} · {f.status} · {f.document_count} docs
            <button type="button" className="ml-4 text-blue-600" onClick={() => void marketplacesyncApi.replayFeed(f.id)}>Replay</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SyncDashboardPage() {
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [channel, setChannel] = useState<MarketplaceChannel>('amazon_spapi');

  useEffect(() => { void marketplacesyncApi.listSync().then(setJobs); }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Synchronization Dashboard</h1>
      <div className="mt-6 flex gap-3">
        <select className="rounded border px-3 py-2 text-sm" value={channel} onChange={(e) => setChannel(e.target.value as MarketplaceChannel)}>
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void marketplacesyncApi.fullSync(channel).then(() => marketplacesyncApi.listSync().then(setJobs))}>Full sync</button>
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void marketplacesyncApi.incrementalSync(channel).then(() => marketplacesyncApi.listSync().then(setJobs))}>Incremental sync</button>
      </div>
      <ul className="mt-6 space-y-2 text-sm">
        {jobs.map((j) => <li key={j.id} className="rounded border bg-white p-3">{j.channel} · {j.sync_type} · {j.status} · processed {j.items_processed}</li>)}
      </ul>
    </div>
  );
}

export function RetryQueuePage() {
  const [items, setItems] = useState<RetryTask[]>([]);
  useEffect(() => { void marketplacesyncApi.retryQueue().then(setItems); }, []);
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Retry Queue</h1>
      <ul className="mt-6 space-y-2 text-sm">
        {items.map((t) => <li key={t.id} className="rounded border bg-white p-3">{t.job_type} {t.job_id} · retry {t.retry_count} · next {t.next_retry_at}</li>)}
      </ul>
    </div>
  );
}

export function DeadLetterQueuePage() {
  const [items, setItems] = useState<DeadLetterEntry[]>([]);
  useEffect(() => { void marketplacesyncApi.deadLetter().then(setItems); }, []);
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Dead Letter Queue</h1>
      <ul className="mt-6 space-y-2 text-sm">
        {items.map((e) => <li key={e.id} className="rounded border bg-white p-3">{e.job_type} {e.job_id}: {e.error_message}</li>)}
      </ul>
    </div>
  );
}

export function ConnectorManagerPage() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  useEffect(() => { void marketplacesyncApi.listConnectors().then(setConnectors); }, []);
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Connector Manager</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {connectors.map((c) => (
          <div key={c.id} className="rounded-xl border bg-white p-4">
            <p className="font-medium">{c.name}</p>
            <p className="text-xs text-slate-500">{c.channel}</p>
            <p className="mt-2 text-sm">Health: {c.health_status}</p>
            <button type="button" className="mt-2 text-sm text-blue-600" onClick={() => void marketplacesyncApi.connectorHealth(c.id)}>Check health</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WebhookMonitorPage() {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  useEffect(() => { void marketplacesyncApi.listWebhooks().then(setEvents); }, []);
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Webhook Monitor</h1>
      <ul className="mt-6 space-y-2 text-sm">
        {events.map((e) => <li key={e.id} className="rounded border bg-white p-3">{e.channel} · {e.event_type} · verified={String(e.verified)}</li>)}
      </ul>
    </div>
  );
}
